import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import api from '../../services/api';
import { uploadFile } from '../../services/upload';

const SuperAdminShops = () => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingShop, setEditingShop] = useState(null);
  const [filters, setFilters] = useState({ name: '', district: '', state: '', status: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const [formData, setFormData] = useState({
    name: '',
    logoUrl: '',
    websiteUrl: '',
    latitude: '',
    longitude: '',
    district: '',
    state: '',
    brandGroupId: '',
    email: '',
    password: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchShops();
  }, [filters, pagination.page]);

  const fetchShops = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Add filters only if they have values
      if (filters.name) params.name = filters.name;
      if (filters.district) params.district = filters.district;
      if (filters.state) params.state = filters.state;
      if (filters.status) {
        // Backend expects status === 'active' || status === 'true' for active, anything else for inactive
        params.status = filters.status === 'active' ? 'active' : 'false';
      }
      
      const response = await api.get('/api/super/shops', { params });
      setShops(response.data.data || []);
      setPagination(response.data.pagination || { page: 1, limit: 10, total: 0, pages: 0 });
    } catch (error) {
      console.error('Error fetching shops:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error fetching shops' });
      setShops([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingShop(null);
    setFormData({
      name: '',
      logoUrl: '',
      websiteUrl: '',
      latitude: '',
      longitude: '',
      district: '',
      state: '',
      brandGroupId: '',
      email: '',
      password: ''
    });
    setLogoFile(null);
    setLogoPreview('');
    setShowModal(true);
  };

  const handleEdit = (shop) => {
    setEditingShop(shop);
    setFormData({
      name: shop.name,
      logoUrl: shop.logoUrl || '',
      websiteUrl: shop.websiteUrl || '',
      latitude: shop.location.latitude,
      longitude: shop.location.longitude,
      district: shop.district,
      state: shop.state,
      brandGroupId: shop.brandGroupId || '',
      email: '',
      password: ''
    });
    setLogoFile(null);
    setLogoPreview(shop.logoUrl || '');
    setShowModal(true);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let finalFormData = { ...formData };

      // Upload logo if a new file is selected
      if (logoFile) {
        const uploadResult = await uploadFile(logoFile, 'logo');
        if (uploadResult.success) {
          finalFormData.logoUrl = uploadResult.url;
        } else {
          setMessage({ type: 'error', text: uploadResult.message || 'Error uploading logo' });
          setUploading(false);
          return;
        }
      }

      if (editingShop) {
        await api.put(`/api/super/shops/${editingShop._id}`, finalFormData);
        setMessage({ type: 'success', text: 'Shop updated successfully' });
      } else {
        await api.post('/api/super/shops', finalFormData);
        setMessage({ type: 'success', text: 'Shop created successfully' });
      }
      setShowModal(false);
      setLogoFile(null);
      setLogoPreview('');
      fetchShops();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error saving shop' });
    } finally {
      setUploading(false);
    }
  };

  const handleToggleStatus = async (shop) => {
    try {
      await api.patch(`/api/super/shops/${shop._id}/status`, {
        isActive: !shop.isActive
      });
      fetchShops();
      setMessage({ type: 'success', text: `Shop ${shop.isActive ? 'deactivated' : 'activated'}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating shop status' });
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  return (
    <div className="app">
      <Navbar />
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Shops Management</h1>
          <button className="btn btn-primary" onClick={handleCreate}>
            Create Shop
          </button>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
            {message.text}
          </div>
        )}

        <div className="filters">
          <div className="form-group">
            <label>Search by Name</label>
            <input
              type="text"
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: e.target.value })}
              placeholder="Shop name"
            />
          </div>
          <div className="form-group">
            <label>District</label>
            <input
              type="text"
              value={filters.district}
              onChange={(e) => setFilters({ ...filters, district: e.target.value })}
              placeholder="District"
            />
          </div>
          <div className="form-group">
            <label>State</label>
            <input
              type="text"
              value={filters.state}
              onChange={(e) => setFilters({ ...filters, state: e.target.value })}
              placeholder="State"
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>District</th>
                <th>State</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shops.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                    No shops found
                  </td>
                </tr>
              ) : (
                shops.map(shop => (
                  <tr key={shop._id}>
                    <td>{shop.name || 'N/A'}</td>
                    <td>{shop.district || 'N/A'}</td>
                    <td>{shop.state || 'N/A'}</td>
                    <td>
                      <span className={shop.isActive ? 'badge-success' : 'badge-danger'}>
                        {shop.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleEdit(shop)}
                        style={{ marginRight: '10px' }}
                      >
                        Edit
                      </button>
                      <button
                        className={`btn ${shop.isActive ? 'btn-danger' : 'btn-success'}`}
                        onClick={() => handleToggleStatus(shop)}
                      >
                        {shop.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span>Page {pagination.page} of {pagination.pages}</span>
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page === pagination.pages}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">
                  {editingShop ? 'Edit Shop' : 'Create Shop'}
                </h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Shop Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Shop Logo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    style={{ marginBottom: '10px' }}
                  />
                  {logoPreview && (
                    <div style={{ marginTop: '10px' }}>
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }}
                      />
                    </div>
                  )}
                  {!logoFile && formData.logoUrl && (
                    <div style={{ marginTop: '10px' }}>
                      <p style={{ fontSize: '12px', color: '#666' }}>Current logo:</p>
                      <img 
                        src={formData.logoUrl} 
                        alt="Current logo" 
                        style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }}
                      />
                    </div>
                  )}
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Or enter URL: <input
                      type="url"
                      value={formData.logoUrl}
                      onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                      placeholder="https://example.com/logo.png"
                      style={{ width: '100%', marginTop: '5px' }}
                    />
                  </p>
                </div>
                <div className="form-group">
                  <label>Website URL</label>
                  <input
                    type="url"
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Latitude *</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Longitude *</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>District *</label>
                  <input
                    type="text"
                    value={formData.district}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>State *</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Brand Group ID</label>
                  <input
                    type="text"
                    value={formData.brandGroupId}
                    onChange={(e) => setFormData({ ...formData, brandGroupId: e.target.value })}
                    placeholder="For linking multiple outlets"
                  />
                </div>
                {!editingShop && (
                  <>
                    <div className="form-group">
                      <label>Admin Email *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Admin Password *</label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        minLength={6}
                      />
                    </div>
                  </>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary" disabled={uploading}>
                    {uploading ? 'Uploading...' : (editingShop ? 'Update' : 'Create')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminShops;

